/* eslint-disable no-unused-vars, array-bracket-newline */
import { IDexTrade } from '@kattana-io/common';
import DesktopTable from '../../../molecules/Table/DesktopTable';
import TickerCell from '../TickerCell';
import Text from '../../../atoms/Text';
import { useTableData } from './useTableData';
import { formatTimestamp, toFixed4, formatTx } from '../formatters';
import { IMyPositionsSelector } from '.';
import { useCallback } from 'react';

export const LS_MY_TRADES_HIDDEN_COLUMNS_KEY =
  'kattana-simple.my_trades_table_columns_hidden';
export const LS_MY_TRADES_KEY = 'kattana-simple.my_trades_table_columns';

interface IMyPositionsTableProps extends IMyPositionsSelector {
  trades: IDexTrade[];
  tickers: any;
  loading: boolean;
  openTx: (tx: string) => void;
  width?: number;
}

const buyClassName = 'row--Buy text--green-600';
const sellClassName = 'row--Sell text--red-600';


/**
 * Render a table
 * @param props
 * @returns
 */
const MyPositionsTable = ({
  tickers,
  trades,
  base,
  base_decimals,
  quote,
  quote_decimals,
  flipped,
  loading,
  openTx,
  width,
}: IMyPositionsTableProps) => {
  const { columns, data, tableColumnsWidth } = useTableData({
    trades,
    quote,
  });

  const headers = ['Date;Action;Price ($);Price;Value;Wallet']; // Headers
  const csvData = [
    ...headers,
    ...trades.map(
      (trade) =>
        `${formatTimestamp(trade.timestamp)};${
          trade.buy === 1 ? 'Buy' : 'Sell'
        };${toFixed4(
          flipped ? trade.price_b_usd : trade.price_a_usd
        )};${toFixed4(flipped ? trade.price_b : trade.price_a)};${toFixed4(
          trade.value_usd
        )};${trade.wallet}`
    ),
  ];

  const renderCell = (cell: any) => {
    const { chain, pair } = cell.row.original;
    const key = `${chain}/${pair}`;

    switch (cell.column.id) {
    case 'ticker':
      return <TickerCell path={key} ticker={tickers[key]} />;
    case 'timestamp':
      return <Text size={12}>{formatTimestamp(cell.value)}</Text>;
    case 'price':
      return (
        <Text size={12}>
          {toFixed4(
            flipped ? cell.row.original.price_b : cell.row.original.price_a
          )}{' '}
          {flipped ? base : quote}
        </Text>
      );
    case 'priceUSD':
      return (
        <Text size={12}>
          {toFixed4(
            flipped
              ? cell.row.original.price_b_usd
              : cell.row.original.price_a_usd
          )}{' '}
            $
        </Text>
      );
    case 'amountIn':
      return (
        <Text size={12}>
          {toFixed4(cell.row.original.amount0 / 10 ** base_decimals)}{' '}
          {flipped ? quote : base}
        </Text>
      );
    case 'amountOut':
      return (
        <Text size={12}>
          {toFixed4(cell.row.original.amount1 / 10 ** quote_decimals)}{' '}
          {flipped ? base : quote}
        </Text>
      );
    case 'tx':
      return (
        <Text
          className='tx'
          size={12}
          onClick={() => openTx(cell.row.original.tx)}
        >
          {formatTx(cell.value)}
        </Text>
      );
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  /**
   * acknowledge flipped when displaying buy/sell
   */
  const getClassNameForRow = useCallback((row: any) => {
    if (row.buy === 1) {
      return flipped ? sellClassName : buyClassName;
    } else {
      return flipped ? buyClassName : sellClassName;
    }
  }, [flipped]);

  return (
    <DesktopTable
      hiddenColumnsKey={LS_MY_TRADES_HIDDEN_COLUMNS_KEY}
      dataKey={LS_MY_TRADES_KEY}
      title='my_trades'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      data={data}
      csvData={csvData}
      loading={loading}
      getClassNameForRow={getClassNameForRow}
      renderCell={renderCell}
    />
  );
};

export default MyPositionsTable;
